/**
 * Simple client-side router for the public site
 */

export interface RouterOptions {
  onNeighborhoodRoute?: (slug: string) => void;
  onListingRoute?: (mlsId: string) => void;
  onPropertyRoute?: (tmk: string) => void;
  onAccountRoute?: () => void;
  onHomeRoute?: () => void;
}

interface Route {
  pattern: RegExp;
  handler: (params: string[]) => void;
}

let routes: Route[] = [];

export function initRouter(options: RouterOptions): void {
  routes = [
    {
      pattern: /^\/neighborhood\/([a-z0-9-]+)$/,
      handler: (params) => options.onNeighborhoodRoute?.(params[0]),
    },
    {
      pattern: /^\/listing\/([a-zA-Z0-9]+)$/,
      handler: (params) => options.onListingRoute?.(params[0]),
    },
    {
      pattern: /^\/property\/([0-9-]+)$/,
      handler: (params) => options.onPropertyRoute?.(params[0]),
    },
    {
      pattern: /^\/account$/,
      handler: () => options.onAccountRoute?.(),
    },
    {
      pattern: /^\/$/,
      handler: () => options.onHomeRoute?.(),
    },
    {
      // The agent-subdomain nav's "Properties" tab - same default map view
      // as "/", just reachable from the persistent header's dedicated map
      // destination. See workers/agent-site/src/index.js's handleGet() for
      // the server-side routing half (this path is explicitly excluded
      // from the SEO-redirect-to-apex shape check there).
      pattern: /^\/properties$/,
      handler: () => options.onHomeRoute?.(),
    },
  ];

  // Handle initial route
  handleRoute(window.location.pathname);

  // Handle popstate (back/forward)
  window.addEventListener('popstate', () => {
    handleRoute(window.location.pathname);
  });
}

function handleRoute(path: string): void {
  updateSeoForPath(path);
  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      route.handler(match.slice(1));
      return;
    }
  }
}

// Keep the canonical + robots tags in sync with the current route. Defaults
// (set in index.html) point everything at the bare home and allow indexing;
// /account adds a noindex because it's a per-user view that shouldn't appear
// in search results. Any other route falls through to the static defaults.
function updateSeoForPath(path: string): void {
  const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');

  if (path === '/account') {
    if (canonical) canonical.href = 'https://slowmaui.com/account';
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = 'noindex,follow';
    return;
  }

  // All other paths → home is canonical, no robots restriction.
  if (canonical) canonical.href = 'https://slowmaui.com/';
  if (robots) robots.remove();
}

export function navigate(path: string): void {
  history.pushState(null, '', path);
  handleRoute(path);
}
